/**
 * controlPanel.ts
 *
 * Builds the on-screen DOM control panel: exaggeration sliders, pressure
 * level range, layer visibility toggles, simulation speed, and legends for
 * the temperature and MSLP color scales. Plain DOM is used (no framework)
 * to keep this prototype dependency-light.
 */

import {
  GFS_PRESSURE_LEVELS_HPA,
  DEFAULT_MIN_HPA,
  DEFAULT_MAX_HPA,
  DEFAULT_HEIGHT_EXAGGERATION,
  MIN_HEIGHT_EXAGGERATION,
  MAX_HEIGHT_EXAGGERATION,
  DEFAULT_VERTICAL_MOTION_EXAGGERATION,
  MIN_VERTICAL_MOTION_EXAGGERATION,
  MAX_VERTICAL_MOTION_EXAGGERATION,
  DONATE_URL
} from "../constants";
import {
  TEMPERATURE_GRADIENT_CSS,
  TEMPERATURE_MIN_K,
  TEMPERATURE_MAX_K,
  MSLP_GRADIENT_CSS,
  MSLP_MIN_HPA,
  MSLP_MAX_HPA
} from "../colorScales";
import type { SurfaceColorMode } from "../scene/globe";

export interface ViewerState {
  heightExaggeration: number;
  verticalMotionExaggeration: number;
  /** Smallest pressure (hPa) currently shown -- the TOP of the visible range. */
  minHpa: number;
  /** Largest pressure (hPa) currently shown -- the BOTTOM/surface of the visible range. */
  maxHpa: number;
  surfaceColorMode: SurfaceColorMode;
  showCoastlines: boolean;
  showLevelShells: boolean;
  showParticles: boolean;
  speedMultiplier: number;
}

export interface ControlPanelHandles {
  state: ViewerState;
  onChange(callback: (state: ViewerState) => void): void;
  setBanner(text: string, isWarning: boolean): void;
}

function kelvinToCelsiusLabel(k: number): string {
  return `${Math.round(k - 273.15)} \u00B0C`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createControlPanel(container: HTMLElement): ControlPanelHandles {
  const state: ViewerState = {
    heightExaggeration: DEFAULT_HEIGHT_EXAGGERATION,
    verticalMotionExaggeration: DEFAULT_VERTICAL_MOTION_EXAGGERATION,
    minHpa: DEFAULT_MIN_HPA,
    maxHpa: DEFAULT_MAX_HPA,
    surfaceColorMode: "temperature",
    showCoastlines: true,
    showLevelShells: false,
    showParticles: true,
    speedMultiplier: 1.5
  };

  const listeners: Array<(state: ViewerState) => void> = [];
  function notify(): void {
    for (const listener of listeners) listener(state);
  }

  // Hidden on desktop; shown fixed top-right on narrow viewports to show/hide the panel, which
  // would otherwise permanently cover most of a phone-sized screen -- see style.css.
  const mobileToggle = el("button", "mobile-menu-toggle", "\u2630");
  mobileToggle.type = "button";
  mobileToggle.setAttribute("aria-label", "Toggle control panel");
  container.appendChild(mobileToggle);

  const panel = el("div", "panel");
  container.appendChild(panel);
  mobileToggle.addEventListener("click", () => {
    panel.classList.toggle("panel-open");
  });

  panel.appendChild(el("h1", "panel-title", "Earth3D"));
  const banner = el(
    "p",
    "panel-banner",
    "Loading atmospheric data..."
  );
  panel.appendChild(banner);

  const actionsRow = el("div", "panel-actions-row");
  panel.appendChild(actionsRow);

  // Hidden entirely (rather than shown with a placeholder/broken link) until a real donate URL
  // is configured -- see DONATE_URL in constants.ts.
  if (DONATE_URL) {
    const donateLink = el("a", "panel-donate-link", "\u2764 Donate");
    donateLink.href = DONATE_URL;
    donateLink.target = "_blank";
    donateLink.rel = "noopener noreferrer";
    actionsRow.appendChild(donateLink);
  }

  const feedbackWidget = buildFeedbackWidget();
  actionsRow.appendChild(feedbackWidget.toggleButton);
  panel.appendChild(feedbackWidget.form);

  // --- Height exaggeration ---------------------------------------------------
  panel.appendChild(buildSliderRow({
    label: "Height exaggeration (terrain + atmosphere)",
    min: MIN_HEIGHT_EXAGGERATION,
    max: MAX_HEIGHT_EXAGGERATION,
    step: 1,
    value: state.heightExaggeration,
    formatValue: (v) => `${v}\u00D7`,
    onInput: (v) => {
      state.heightExaggeration = v;
      notify();
    }
  }));

  // --- Vertical motion exaggeration -------------------------------------------
  // Exponential scale (see buildSliderRow's "exponential" option): equal slider movement means
  // equal PERCENTAGE change in multiplier, giving fine control near 1x (where small differences
  // in a subtle effect matter most) while still comfortably reaching 10x at the other end.
  panel.appendChild(buildSliderRow({
    label: "Vertical motion exaggeration",
    min: MIN_VERTICAL_MOTION_EXAGGERATION,
    max: MAX_VERTICAL_MOTION_EXAGGERATION,
    scale: "exponential",
    value: state.verticalMotionExaggeration,
    formatValue: (v) => `${v.toFixed(1)}\u00D7`,
    onInput: (v) => {
      state.verticalMotionExaggeration = v;
      notify();
    }
  }));

  // --- Vertical motion note ------------------------------------------------------------
  const vert_mot_note = el("p", "panel-vert_mot_note");
  vert_mot_note.appendChild(document.createTextNode("Vertical motion exaggeration multiplies upward motion to enhance upflow visibility but produces physically inconsistent flow height when not 1x"));
  panel.appendChild(vert_mot_note);

  // --- Simulation speed --------------------------------------------------------
  panel.appendChild(buildSliderRow({
    label: "Simulation speed",
    min: 0.1,
    max: 5,
    step: 0.1,
    value: state.speedMultiplier,
    formatValue: (v) => `${v.toFixed(1)}\u00D7`,
    onInput: (v) => {
      state.speedMultiplier = v;
      notify();
    }
  }));

  // --- Pressure level range ----------------------------------------------------
  const rangeRow = el("div", "control-row");
  rangeRow.appendChild(el("label", "control-label", "Visible pressure range"));
  const selectRow = el("div", "select-row");

  const bottomSelect = buildLevelSelect(state.maxHpa);
  const topSelect = buildLevelSelect(state.minHpa);
  selectRow.appendChild(labeledSelect("Bottom", bottomSelect));
  selectRow.appendChild(labeledSelect("Top", topSelect));
  rangeRow.appendChild(selectRow);
  panel.appendChild(rangeRow);

  function syncRangeFromSelects(): void {
    let bottom = Number(bottomSelect.value);
    let top = Number(topSelect.value);
    if (top > bottom) {
      // Keep "top" numerically <= "bottom" (hPa decreases with altitude).
      [top, bottom] = [bottom, top];
      topSelect.value = String(top);
      bottomSelect.value = String(bottom);
    }
    state.minHpa = top;
    state.maxHpa = bottom;
    notify();
  }
  bottomSelect.addEventListener("input", syncRangeFromSelects);
  topSelect.addEventListener("input", syncRangeFromSelects);

  // --- Surface color mode ---------------------------------------------------------
  const surfaceColorSelect = el("select", "level-select");
  const surfaceColorOptions: Array<{ value: SurfaceColorMode; label: string }> = [
    { value: "none", label: "Altitude" },
    { value: "mslp", label: "Pressure" },
    { value: "temperature", label: "Temperature" }
  ];
  for (const option of surfaceColorOptions) {
    const optionEl = el("option", undefined, option.label);
    optionEl.value = option.value;
    if (option.value === state.surfaceColorMode) optionEl.selected = true;
    surfaceColorSelect.appendChild(optionEl);
  }
  surfaceColorSelect.addEventListener("input", () => {
    state.surfaceColorMode = surfaceColorSelect.value as SurfaceColorMode;
    notify();
  });
  panel.appendChild(labeledSelect("Surface color", surfaceColorSelect));

  // --- Layer toggles ------------------------------------------------------------
  const togglesRow = el("div", "control-row");
  togglesRow.appendChild(el("label", "control-label", "Layers"));
  togglesRow.appendChild(buildCheckbox("Coastlines", state.showCoastlines, (v) => {
    state.showCoastlines = v;
    notify();
  }));
  togglesRow.appendChild(buildCheckbox("Atmosphere level shells", state.showLevelShells, (v) => {
    state.showLevelShells = v;
    notify();
  }));
  togglesRow.appendChild(buildCheckbox("Wind particles", state.showParticles, (v) => {
    state.showParticles = v;
    notify();
  }));
  panel.appendChild(togglesRow);

  // --- Legends --------------------------------------------------------------------
  panel.appendChild(buildLegend("Particle temperature", TEMPERATURE_GRADIENT_CSS, kelvinToCelsiusLabel(TEMPERATURE_MIN_K), kelvinToCelsiusLabel(TEMPERATURE_MAX_K)));
  const mslpLegend = buildLegend("Surface MSLP", MSLP_GRADIENT_CSS, `${MSLP_MIN_HPA} hPa`, `${MSLP_MAX_HPA} hPa`);
  panel.appendChild(mslpLegend);
  function updateMslpLegendVisibility(): void {
    mslpLegend.style.display = state.surfaceColorMode === "mslp" ? "" : "none";
  }
  updateMslpLegendVisibility();
  surfaceColorSelect.addEventListener("input", updateMslpLegendVisibility);

  // --- Attribution ------------------------------------------------------------
  const attribution = el("p", "panel-attribution");
  attribution.appendChild(document.createTextNode("Inspired by "));
  const nullschoolLink = el("a", undefined, "earth.nullschool.net");
  nullschoolLink.href = "https://earth.nullschool.net";
  nullschoolLink.target = "_blank";
  nullschoolLink.rel = "noopener noreferrer";
  attribution.appendChild(nullschoolLink);
  attribution.appendChild(
    document.createTextNode(" -- real-vector coastlines, color scales, and visualization methods are based on its open-source client.")
  );
  panel.appendChild(attribution);

  return {
    state,
    onChange: (callback) => listeners.push(callback),
    setBanner: (text, isWarning) => {
      banner.textContent = text;
      banner.classList.toggle("panel-banner-warning", isWarning);
    }
  };
}

// --- DOM building helpers -----------------------------------------------------------

function buildSliderRow(options: {
  label: string;
  min: number;
  max: number;
  /** Required for the default "linear" scale; ignored for "exponential" (see below). */
  step?: number;
  value: number;
  formatValue: (v: number) => string;
  onInput: (v: number) => void;
  /**
   * "linear" (default): the native <input type=range> value IS the applied value directly.
   * "exponential": equal slider movement corresponds to equal PERCENTAGE change in the applied
   * value (not equal absolute change) -- e.g. moving the slider the same small amount near the
   * low end changes the value by the same ~5% whether that's 1.0->1.05 or 5.0->5.25. This suits
   * a wide multiplicative range where fine control matters most near the low end but the high
   * end still needs to be reachable without an impractically long slider. Internally the
   * native <input> operates on a normalized 0..1 position; only the DISPLAYED/applied value
   * (computed via `min * (max/min)^position`) is exponential. Requires min > 0.
   */
  scale?: "linear" | "exponential";
}): HTMLElement {
  const row = el("div", "control-row");
  const labelEl = el("label", "control-label");
  const valueEl = el("span", "control-value", options.formatValue(options.value));
  labelEl.textContent = `${options.label}: `;
  labelEl.appendChild(valueEl);
  row.appendChild(labelEl);

  const input = el("input", "control-slider");
  input.type = "range";

  if (options.scale === "exponential") {
    const ratio = options.max / options.min;
    const toPosition = (value: number) => Math.log(value / options.min) / Math.log(ratio);
    const toValue = (position: number) => options.min * Math.pow(ratio, position);
    const EXPONENTIAL_STEPS = 1000; // fine enough to feel continuous
    input.min = "0";
    input.max = "1";
    input.step = String(1 / EXPONENTIAL_STEPS);
    input.value = String(toPosition(options.value));
    input.addEventListener("input", () => {
      const v = toValue(Number(input.value));
      valueEl.textContent = options.formatValue(v);
      options.onInput(v);
    });
  } else {
    input.min = String(options.min);
    input.max = String(options.max);
    input.step = String(options.step ?? 1);
    input.value = String(options.value);
    input.addEventListener("input", () => {
      const v = Number(input.value);
      valueEl.textContent = options.formatValue(v);
      options.onInput(v);
    });
  }
  row.appendChild(input);
  return row;
}

function buildLevelSelect(selectedHpa: number): HTMLSelectElement {
  const select = el("select", "level-select");
  // GFS_PRESSURE_LEVELS_HPA is ordered surface-to-top (1000 -> 0.01); present it that way.
  for (const hPa of GFS_PRESSURE_LEVELS_HPA) {
    const option = el("option");
    option.value = String(hPa);
    option.textContent = `${hPa} hPa`;
    if (hPa === selectedHpa) option.selected = true;
    select.appendChild(option);
  }
  return select;
}

function labeledSelect(label: string, select: HTMLSelectElement): HTMLElement {
  const wrapper = el("div", "select-wrapper");
  wrapper.appendChild(el("span", "select-label", label));
  wrapper.appendChild(select);
  return wrapper;
}

function buildCheckbox(label: string, checked: boolean, onChange: (v: boolean) => void): HTMLElement {
  const wrapper = el("label", "checkbox-row");
  const input = el("input");
  input.type = "checkbox";
  input.checked = checked;
  input.addEventListener("change", () => onChange(input.checked));
  wrapper.appendChild(input);
  wrapper.appendChild(document.createTextNode(` ${label}`));
  return wrapper;
}

function buildLegend(title: string, gradientCss: string, minLabel: string, maxLabel: string): HTMLElement {
  const wrapper = el("div", "legend");
  wrapper.appendChild(el("div", "legend-title", title));
  const bar = el("div", "legend-bar");
  bar.style.background = gradientCss;
  wrapper.appendChild(bar);
  const labelsRow = el("div", "legend-labels");
  labelsRow.appendChild(el("span", undefined, minLabel));
  labelsRow.appendChild(el("span", undefined, maxLabel));
  wrapper.appendChild(labelsRow);
  return wrapper;
}

/**
 * A collapsible "Feedback" widget: a toggle button that reveals a small form (message +
 * optional reply-to email), POSTing to /api/feedback (same-origin, reverse-proxied by nginx to
 * a local relay service -- see scripts/feedback-server.mjs). Fails gracefully with a visible
 * error message if that endpoint isn't available (e.g. during local dev against `vite`, which
 * has no backend), rather than silently doing nothing. The toggle button and form are returned
 * separately so the caller can place them in different parts of the panel layout (button next
 * to Donate, form spanning the full panel width below).
 */
function buildFeedbackWidget(): { toggleButton: HTMLButtonElement; form: HTMLElement } {
  const toggleButton = el("button", "panel-feedback-toggle", "\uD83D\uDCAC Feedback");
  toggleButton.type = "button";

  const form = el("div", "feedback-form");
  form.style.display = "none";

  const textarea = el("textarea", "feedback-textarea");
  textarea.placeholder = "What's working, what's not, what would make this better?";
  textarea.rows = 4;
  textarea.maxLength = 5000;
  form.appendChild(textarea);

  const emailInput = el("input", "feedback-email");
  emailInput.type = "email";
  emailInput.placeholder = "Your email (optional, if you'd like a reply)";
  form.appendChild(emailInput);

  const submitButton = el("button", "feedback-submit", "Send");
  submitButton.type = "button";
  form.appendChild(submitButton);

  const status = el("div", "feedback-status");
  form.appendChild(status);

  toggleButton.addEventListener("click", () => {
    form.style.display = form.style.display === "none" ? "flex" : "none";
  });

  submitButton.addEventListener("click", () => {
    const message = textarea.value.trim();
    if (!message) {
      status.textContent = "Please write a message first.";
      status.classList.add("feedback-status-error");
      return;
    }
    submitButton.disabled = true;
    status.classList.remove("feedback-status-error");
    status.textContent = "Sending...";

    fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, contactEmail: emailInput.value.trim() })
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `Request failed (HTTP ${response.status})`);
        }
        status.textContent = "Thanks! Your feedback was sent.";
        textarea.value = "";
        emailInput.value = "";
      })
      .catch((err: unknown) => {
        status.classList.add("feedback-status-error");
        status.textContent = err instanceof Error ? err.message : "Failed to send feedback.";
      })
      .finally(() => {
        submitButton.disabled = false;
      });
  });

  return { toggleButton, form };
}
